import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getUsers, updateUserLevel, type User } from '../api/client';
import { clsx } from 'clsx';

const levelColors: Record<string, string> = {
  ic1: 'bg-gray-100 text-gray-600',
  ic2: 'bg-gray-100 text-gray-700',
  staff: 'bg-blue-100 text-blue-700',
  sr_staff: 'bg-blue-100 text-blue-800',
  manager: 'bg-yellow-100 text-yellow-700',
  sr_manager: 'bg-yellow-100 text-yellow-800',
  director: 'bg-purple-100 text-purple-700',
  vp: 'bg-purple-100 text-purple-800',
  svp: 'bg-indigo-100 text-indigo-700',
  c_suite: 'bg-red-100 text-red-700',
};

const levels = ['ic1', 'ic2', 'staff', 'sr_staff', 'manager', 'sr_manager', 'director', 'vp', 'svp', 'c_suite'];

export default function Users() {
  const queryClient = useQueryClient();
  const { data: users, isLoading } = useQuery({ queryKey: ['users'], queryFn: getUsers });

  const updateLevelMutation = useMutation({
    mutationFn: ({ id, level }: { id: string; level: string }) => updateUserLevel(id, level),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
    },
  });

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Users</h1>
        <p className="text-gray-500 mt-1">Manage user levels and permissions</p>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Level
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users?.map((user: User) => (
                <tr key={user.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-medium">
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.name}
                        </div>
                        <div className="text-sm text-gray-500">
                          {user.slack_id}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.email}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={clsx(
                        'px-2 py-1 rounded text-xs font-medium',
                        levelColors[user.level] || 'bg-gray-100 text-gray-600'
                      )}
                    >
                      {user.level}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <select
                      value={user.level}
                      onChange={(e) =>
                        updateLevelMutation.mutate({ id: user.id, level: e.target.value })
                      }
                      className="px-3 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-primary-500"
                    >
                      {levels.map((level) => (
                        <option key={level} value={level}>
                          {level}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
